import React from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { wrapFunctionalComponent } from 'utils/framework';
import { Grid, Flex } from 'components/ui';
import SwatchesDisplay from 'components/ProductPage/Swatches/SwatchesDisplay';
import SwatchDescription from 'components/ProductPage/Swatches/SwatchDescription';
import skuUtils from 'utils/Sku';
import Wizard from 'components/ProductPage/Wizard/Wizard';
import StartScreen from 'components/ShadeFinder/StartScreen/StartScreen';
import BrandsList from 'components/ShadeFinder/BrandsList/BrandsList';
import ProductSelection from 'components/ShadeFinder/ProductSelection/ProductSelection';
import SkuSelection from 'components/ShadeFinder/SkuSelection/SkuSelection';
import ResultsScreen from 'components/ShadeFinder/ResultsScreen/ResultsScreen';
import localeUtils from 'utils/LanguageLocale';
import swatchUtils from 'utils/Swatch';
import memoizeOne from 'utils/memoizeOne';
import SwatchesEN from 'components/ProductPage/Swatches/locales/Swatches_en_US';
import SwatchesFR from 'components/ProductPage/Swatches/locales/Swatches_fr_CA';

const {
    createRefinementGroups, buildSwatchGroupsAndFilters, handleSkuOnClick, CLOSEST, CLOSE
} = swatchUtils;
const getText = localeUtils.getLocalisedDataFromResource(SwatchesEN, SwatchesFR);

const EMPTY_OBJ = {};

const memoizedBuildSwatchGroupsAndFilters = memoizeOne(buildSwatchGroupsAndFilters);
const memoizedCreateRefinementGroups = memoizeOne(createRefinementGroups);

const deriveWizardState = wizard => {
    const result = wizard.result || false;
    const currentPage = wizard.currentPage || 0;
    const isOpen = wizard.showShadeFinderQuizModal || false;
    let matchText = wizard.matchText || false;

    if (result && Object.entries(result).length) {
        matchText = matchText.split(' ')[0].toLowerCase();

        if (matchText === CLOSEST) {
            matchText = CLOSE;
        }
    }

    return {
        wizardResult: result,
        wizardMatchText: matchText,
        wizardCurrentPage: currentPage,
        isWizardOpen: isOpen
    };
};

const Swatches = ({
    loveIcon, isCustomSet, isSkuReady, currentProduct, colorIQMatch, isSmallView, ...rest
}) => {
    const wizard = useSelector(state => state.wizard, shallowEqual);

    const { skuSelectorType } = currentProduct;

    if (!skuSelectorType) {
        return null;
    }

    const isNoneType = skuSelectorType === skuUtils.skuSwatchType.NONE;
    const { wizardResult, wizardMatchText, wizardCurrentPage, isWizardOpen } = deriveWizardState(wizard);

    const swatchGroupsAndFilters = memoizedBuildSwatchGroupsAndFilters(currentProduct ? memoizedCreateRefinementGroups(currentProduct) : EMPTY_OBJ);

    return (
        <>
            {isNoneType || (
                <>
                    <p
                        id={skuUtils.ARIA_DESCRIBED_BY_IDS.COLOR_SWATCH}
                        style={{ display: 'none' }}
                    >
                        {`${getText('instructionPrefix')} ${getText('color')}`}
                    </p>
                    <p
                        id={skuUtils.ARIA_DESCRIBED_BY_IDS.SIZE_SWATCH}
                        style={{ display: 'none' }}
                    >
                        {`${getText('instructionPrefix')} ${getText('size')}`}
                    </p>
                </>
            )}

            {isCustomSet ? null : isNoneType ? (
                <Grid
                    columns={loveIcon && ['1fr auto', null, 1]}
                    gap={loveIcon ? [3, null, 0] : null}
                    css={{ '&:empty': { display: 'none' } }}
                >
                    <SwatchDescription />
                    {loveIcon && isSmallView && <Flex justifyContent={'end'}>{loveIcon}</Flex>}
                </Grid>
            ) : (
                <Grid gap={2}>
                    <SwatchesDisplay
                        isSkuReady={isSkuReady}
                        loveIcon={loveIcon}
                        showColorMatch={false}
                        wizardResult={wizardResult}
                        wizardMatchText={wizardMatchText}
                        product={currentProduct}
                        {...swatchGroupsAndFilters}
                        colorIQMatch={colorIQMatch}
                        {...rest}
                    />
                </Grid>
            )}

            {currentProduct.isReverseLookupEnabled && (
                <Wizard
                    isActive={false}
                    isOpen={isWizardOpen}
                    currentPage={wizardCurrentPage}
                    content={[
                        <StartScreen />,
                        <BrandsList />,
                        <ProductSelection />,
                        <SkuSelection />,
                        <ResultsScreen
                            currentProduct={currentProduct}
                            handleSkuOnClick={handleSkuOnClick}
                        />
                    ]}
                    resetOnClose={true}
                    modalTitle={getText('shadeFinder')}
                />
            )}
        </>
    );
};

export default wrapFunctionalComponent(Swatches, 'Swatches');
